import classNames from 'classnames'
import DOMPurify from 'dompurify'
import { Link } from 'gatsby'
import { useMemo } from 'react'
import { Picture } from 'src/components/ui/Image'
import type {
  SegmentationCarouselWithImageCard,
  SegmentationCarouselWithImageTheme,
} from 'src/types/segmentationCarouselWithImage'
import isSafeLink from 'src/utils/isSafeLink'

interface SegmentationImageCardProps {
  card: SegmentationCarouselWithImageCard
  theme: SegmentationCarouselWithImageTheme
}

const BLOCKED_TAGS = ['style', 'script', 'iframe', 'object', 'embed', 'form']
const ALLOWED_TEXT_TAGS = ['strong', 'em', 'b', 'i', 'br', 'span']

const sanitizeSecondaryText = (content?: string) => {
  if (typeof DOMPurify?.sanitize !== 'function') {
    return ''
  }

  return DOMPurify.sanitize(content ?? '', {
    ALLOWED_TAGS: ALLOWED_TEXT_TAGS,
    ALLOWED_ATTR: [],
    FORBID_TAGS: BLOCKED_TAGS,
    FORBID_ATTR: ['style'],
  })
}

export default function SegmentationImageCard({
  card,
  theme,
}: SegmentationImageCardProps) {
  const {
    imageProperties,
    textPrimary,
    textSecondary,
    link,
    firstButton,
    secondButton,
  } = card

  const safeSecondaryText = useMemo(
    () => sanitizeSecondaryText(textSecondary),
    [textSecondary]
  )

  const cardLink = isSafeLink(link) ? link?.trim() : undefined

  const buttons = [firstButton, secondButton].filter((button) => {
    return Boolean(button?.label?.trim() && isSafeLink(button?.link))
  })

  const linkedButtonsCount = buttons.length
  const hasButtons = linkedButtonsCount > 0
  const shouldActivateCardLink = Boolean(cardLink) && linkedButtonsCount <= 1

  const cardClassName = classNames(
    'flex items-end group relative block h-full w-full overflow-hidden bg-restructure-background-primary-inverted',
    shouldActivateCardLink ? 'cursor-pointer' : 'cursor-default'
  )

  const cardLinkClassName = classNames(
    'absolute inset-0 z-[1] block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    theme === 'dark'
      ? 'focus-visible:ring-white focus-visible:ring-offset-darkThemeComponents'
      : 'focus-visible:ring-fullDark focus-visible:ring-offset-white'
  )

  const content = (
    <>
      <Picture
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]"
        sources={[
          {
            src: imageProperties?.image ?? '',
            width: 320,
            height: 437,
            media: '(max-width: 767px)',
          },
          {
            src: imageProperties?.image ?? '',
            width: 339.84,
            height: 464.44,
            media: '(min-width: 768px)',
          },
        ]}
        img={{
          src: imageProperties?.image ?? '',
          width: 339.84,
          height: 464.44,
          alt: imageProperties?.alt ?? textPrimary ?? 'Card de segmentação',
          loading: 'lazy',
        }}
        shouldCompress={false}
      />

      <div
        className={classNames(
          'w-full max-h-[193px] relative mt-auto flex h-full flex-col justify-end gap-2 px-[18px] pb-5 text-white md:gap-2.5 bg-gradient-to-b from-black/0 via-black/10 to-black/85',
          shouldActivateCardLink ? 'z-[2] pointer-events-none' : 'z-[1]'
        )}
      >
        {textPrimary?.trim() && (
          <h3 className="text-[20px] font-extrabold uppercase leading-[1.1] tracking-[0.2px] text-left">
            {textPrimary}
          </h3>
        )}

        {safeSecondaryText && (
          <p
            className="max-w-[228px] text-[13.5px] leading-[18.9px] text-restructure-tertiary-inverted md:max-w-[230px] text-left"
            dangerouslySetInnerHTML={{ __html: safeSecondaryText }}
          />
        )}

        {hasButtons && (
          <div
            className={classNames(
              'mt-[6px] flex flex-wrap items-center gap-2',
              shouldActivateCardLink && 'pointer-events-auto'
            )}
          >
            {buttons.map((button, index) => {
              const buttonLabel = button?.label?.trim() ?? ''
              const buttonLink = button?.link?.trim() ?? ''

              return (
                <Link
                  key={`${buttonLabel}-${index}`}
                  to={buttonLink}
                  className={classNames(
                    'inline-flex min-h-[32px] items-center justify-center rounded-full border px-[15px] py-2 text-[12px] font-bold leading-none transition-colors',
                    index === 0
                      ? 'border-white bg-white text-restructure-primary hover:bg-white/90'
                      : 'border-white bg-transparent text-white hover:bg-white/15'
                  )}
                >
                  {buttonLabel}
                </Link>
              )
            })}
          </div>
        )}
      </div>
    </>
  )

  return (
    <article
      data-testid={
        shouldActivateCardLink ? undefined : 'segmentation-image-card'
      }
      className={cardClassName}
    >
      {shouldActivateCardLink && cardLink && (
        <Link
          data-testid="segmentation-image-card"
          to={cardLink}
          className={cardLinkClassName}
          aria-label={
            textPrimary?.trim() ? textPrimary.trim() : 'Acessar segmento'
          }
        />
      )}
      {content}
    </article>
  )
}
